// src/finance/scenarios.ts
// Les trois scénarios du calculateur (100 % salaire / 100 % dividendes / mix),
// simulés dans les deux juridictions, et le choix du « plus avantageux ».
//
// Extrait de CalculatorPage : le repli des cibles inatteignables y était
// indissociable du rendu, donc intestable — et il fabriquait un RegimeResult
// « à perte » qui contredisait la bannière posée juste au-dessus.
import { simulateRegime } from './engine/engine'
import { composeRevenue, findServiceRevenueForNet } from './serviceRevenue'
import type { CorporateRegime, FinanceDirection, FinanceSettings, RegimeResult } from './engine/types'

export type ScenarioKey = 'fullSalary' | 'fullDividends' | 'mix'

/** Libellés des cartes — partagés avec la bannière et le bandeau du Suivi. */
export const SCENARIO_LABELS: Record<ScenarioKey, string> = {
  fullSalary: '100 % salaire',
  fullDividends: '100 % dividendes',
  mix: 'Mix salaire + dividendes',
}

export type ScenarioSet = Record<ScenarioKey, RegimeResult>

export type Scenarios = {
  uk: ScenarioSet
  fr: ScenarioSet
  /**
   * Par juridiction puis par scénario : true quand AUCUN CA n'atteint le net visé
   * (`findRevenueForNet` a rendu `null`). Un booléen unique partagé par les six
   * simulations faisait dire à la bannière « aucun chiffre d'affaires ne permet
   * d'atteindre ce net » alors que deux cartes sur trois affichaient un CA
   * parfaitement atteignable.
   */
  unreachable: Record<CorporateRegime, Record<ScenarioKey, boolean>>
  /**
   * CA de PRESTATION par juridiction et par scénario — le CA total moins le brut
   * de l'app. C'est la seule grandeur à laquelle un TJM ou un CA horaire puisse
   * s'appliquer : un abonnement ne dépend pas du temps facturé.
   */
  serviceRevenue: Record<CorporateRegime, Record<ScenarioKey, number>>
}

const SHARE_OF = (settings: FinanceSettings): Record<ScenarioKey, number> => ({
  fullSalary: 1,
  fullDividends: 0,
  mix: settings.mixSalaryShare,
})

export function computeScenarios(settings: FinanceSettings, companyExpensesMonthly: number): Scenarios {
  const shares = SHARE_OF(settings)
  const unreachable: Record<CorporateRegime, Record<ScenarioKey, boolean>> = {
    uk: { fullSalary: false, fullDividends: false, mix: false },
    fr: { fullSalary: false, fullDividends: false, mix: false },
  }
  const serviceRevenue: Record<CorporateRegime, Record<ScenarioKey, number>> = {
    uk: { fullSalary: 0, fullDividends: 0, mix: 0 },
    fr: { fullSalary: 0, fullDividends: 0, mix: 0 },
  }

  const compute = (key: ScenarioKey, regime: CorporateRegime): RegimeResult => {
    const share = shares[key]
    // Le moteur ne connaît qu'un CA et des charges : la composition des deux
    // sources (prestation + app, commission Apple en charge) se fait ici.
    const simulate = (service: number): RegimeResult => {
      serviceRevenue[regime][key] = service
      const c = composeRevenue(settings, service, companyExpensesMonthly)
      return simulateRegime(c.revenueTotal, share, settings, c.expensesTotal, regime)
    }
    if (settings.direction !== 'netToRevenue') {
      // Le montant saisi est le CA de PRESTATION : l'app s'y ajoute, elle ne s'y
      // substitue pas — sans quoi augmenter ses revenus d'app réduirait sa facturation.
      return simulate(settings.targetAmount)
    }
    const service = findServiceRevenueForNet(settings.targetAmount, share, settings, companyExpensesMonthly, regime)
    if (service !== null) {
      return simulate(service)
    }
    unreachable[regime][key] = true
    serviceRevenue[regime][key] = 0
    // Repli TOUT À ZÉRO, dépenses société COMPRISES. Simuler un CA nul en gardant
    // les dépenses posait `isLoss = 0 − dépenses < 0` : la carte annonçait alors
    // « À perte : le CA ne couvre pas les dépenses société » et un déficit égal aux
    // dépenses, alors qu'il n'y a ni CA nul ni perte — seulement un jeu de taux
    // dont aucun CA ne sort le net visé. À dépenses nulles, l'enveloppe vaut 0 et
    // `isLoss` reste faux : les montants sont des blancs, pas un diagnostic.
    return simulateRegime(0, share, settings, 0, regime)
  }

  const set = (regime: CorporateRegime): ScenarioSet => ({
    fullSalary: compute('fullSalary', regime),
    fullDividends: compute('fullDividends', regime),
    mix: compute('mix', regime),
  })

  return { uk: set('uk'), fr: set('fr'), unreachable, serviceRevenue }
}

/**
 * Scénario le plus avantageux de la juridiction affichée : CA le plus bas à net
 * égal (`netToRevenue`), net le plus haut à CA égal (`revenueToNet`).
 *
 * Les scénarios hors de portée sont ÉCARTÉS : leur repli à 0 € battait
 * mécaniquement tout CA réel au score (−0 > −8 787,83), si bien que le badge
 * « le plus avantageux » désignait toujours le scénario impossible. `null` quand
 * aucun scénario n'est atteignable — il n'y a alors rien à recommander.
 */
export function bestScenarioKey(
  scenarios: Scenarios,
  direction: FinanceDirection,
  regime: CorporateRegime = 'uk',
): ScenarioKey | null {
  const keys: ScenarioKey[] = ['fullSalary', 'fullDividends', 'mix']
  const score = (r: RegimeResult): number => (direction === 'netToRevenue' ? -r.revenueMonthly : r.netMonthly)
  let best: ScenarioKey | null = null
  for (const key of keys) {
    if (scenarios.unreachable[regime][key]) continue
    if (best === null || score(scenarios[regime][key]) > score(scenarios[regime][best])) best = key
  }
  return best
}
